import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { LearningRecordCourse, LearningRecordSimulation } from "src/types/coaching/learners";
import React, { useEffect } from "react";

import _ from 'lodash';
import { useDispatch } from "react-redux";

import {
  completeSimulation,
  fetchLearningRecordSimulation,
  fetchSimulationReport,
  invalidateCompleteSimulation,
  invalidateRestartSimulation,
  invalidateStartSimulation,
  restartSimulation,
  startSimulation
} from "src/redux/actions/coaching/learners";

import { SimulationSummaryComponent } from "src/pages/coaching/components/simulation/view";
import { SimulationContext, SimulationContextProps } from "src/pages/coaching/components/simulation/view/context";

import userRoleService from "src/services/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";

import styles from "./preview.module.scss";
import { fetchDraftSimulation } from "src/redux/actions/coaching/simulation";
import { Simulation } from "src/types/coaching/simulation";
import { SimulationNotFound } from "src/pages/coaching/components/notFound";

interface PreviewSimulationState {
  simulation: LearningRecordSimulation | null;
}

export const PreviewSimulationPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const simulationId = params.id;

  const [previewSimulation, setPreviewSimulation] = React.useState<PreviewSimulationState>({
    simulation: null,
  });

  const loading = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${simulationId}].loading`);
  });

  const simulation: Simulation = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${simulationId}].item`) as Simulation;
  });

  const simulationErrorCode = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${simulationId}].error.errors[0].code`);
  });

  const startingSimulation = useSelector((state: any) => {
    const starting = _.get(state.learners, `simulation[${simulationId}].start.starting`);
    return starting;
  });

  const simulationStarted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].start.started`);
  });

  const restartingSimulation = useSelector((state: any) => {
    const starting = _.get(state.learners, `simulation[${simulationId}].restart.restarting`);
    return starting;
  });

  const simulationRestarted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].restart.restarted`);
  });

  const completingSimulation = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].complete.completing`);
  });

  const simulationCompleted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].complete.completed`);
  });

  const fetchingSimulationReport = useSelector((state: any) => {
    const starting = _.get(state.learners, `simulation[${simulationId}].report.fetching`);
    return starting;
  });

  const fetchedSimulationReport = useSelector((state: any) => {
    const starting = _.get(state.learners, `simulation[${simulationId}].report.fetched`);
    return starting;
  });

  const simulationAssessmentReport = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].item.report`);
  });

  const [courseContext, setCourseContext] = React.useState<SimulationContextProps>({
    id: simulationId,
    view: "summary",
  });


  const handleChangeView = (view: "summary" | "simulator" | 'completion' | "report" | "failed") => {
    setCourseContext((prev) => {
      return {
        ...prev,
        view: view,
      }
    });
  }

  const handleBackClicked = () => {
    navigate(`/coaching/courses`);
  }

  const handleStartCourse = () => {
    handleChangeView("simulator");
  }

  const handleRestartCourse = () => {

  }

  const handleEndCourse = (simulation: LearningRecordSimulation) => {
    handleChangeView("summary");
  };

  const handleViewReport = () => {
    handleChangeView("report");
  }

  useEffect(() => {
    if (simulationId && !simulation) {
      dispatch(fetchDraftSimulation(simulationId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: simulationId,
        view: "summary",
      }
    });
  }, [simulationId]);

  useEffect(() => {
    if (!simulation) {
      return;
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        onBack: handleBackClicked,
        startSimulation: handleStartCourse,
        endSimulation: handleEndCourse,
        restartSimulation: handleRestartCourse,
        viewReport: handleViewReport,
        changeView: handleChangeView,
      }
    });

    setPreviewSimulation((prev) => {
      return {
        ...prev,
        simulation: {
          id: simulation.id!,
          simulation: simulation,
          status: "NOT_STARTED",
        },
      }
    });
  }, [simulation]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        startingSimulation: startingSimulation
      }
    });
  }, [startingSimulation]);

  useEffect(() => {
    if (simulationStarted) {
      handleChangeView("simulator");
    }
  }, [simulationStarted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        restartingSimulation: restartingSimulation
      }
    });
  }, [restartingSimulation]);

  useEffect(() => {
    if (simulationRestarted) {
      handleChangeView("simulator");
    }
  }, [simulationRestarted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        endingSimulation: completingSimulation
      }
    });
  }, [completingSimulation]);

  useEffect(() => {
    if (simulationCompleted) {
      const status = _.get(simulation, "report.status");
      if (status === "in_sufficient_duration") {
        handleChangeView("failed");
      } else {
        handleChangeView("completion");
      }
    }
  }, [simulationCompleted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        fetchingReport: fetchingSimulationReport,
        fetchedReport: fetchedSimulationReport,
        assessmentReport: simulationAssessmentReport,
      }
    });

    if (fetchedSimulationReport) {
      handleChangeView("report");
    }
  }, [fetchingSimulationReport, fetchedSimulationReport, simulationAssessmentReport]);

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (simulationErrorCode === 10007) {
    return <SimulationNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }} />
  }

  if ((loading && previewSimulation.simulation?.id != simulationId) || !previewSimulation.simulation) {
    return <div className={styles.loading}>Loading simulation...</div>
  }

  return (
    <SimulationContext.Provider value={courseContext}>
      <SimulationSummaryComponent simulation={previewSimulation.simulation!} />
    </SimulationContext.Provider>
  );
}